import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Avatar,
  Typography,
  Modal,
  Descriptions,
  Space,
  Badge,
  Statistic,
  Divider,
  Tag,
  Button,
  notification,
  Tooltip,
  Spin,
  Empty,
  Input,
  Popconfirm,
} from "antd";
import {
  InfoCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  InstagramOutlined,
  EnvironmentOutlined,
  LikeOutlined,
  CommentOutlined,
  FileImageOutlined,
  CalendarOutlined,
  ManOutlined,
  WomanOutlined,
  BellOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { Search } = Input;

const Customers = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);

  // Fonction pour calculer l'âge à partir de la date de naissance
  const calculerAge = (dateNaissance) => {
    if (!dateNaissance) return "N/A";
    const ajd = new Date();
    const dateNaiss = new Date(dateNaissance);
    let age = ajd.getFullYear() - dateNaiss.getFullYear();
    const mois = ajd.getMonth() - dateNaiss.getMonth();
    if (mois < 0 || (mois === 0 && ajd.getDate() < dateNaiss.getDate())) {
      age--;
    }
    return age;
  };

  // Fonction pour calculer le taux d'engagement moyen
  const calculerTauxEngagement = (utilisateurId, username) => {
    const postesUtilisateur = posts.filter(
      (post) =>
        post.userId?._id === utilisateurId ||
        post.userId === utilisateurId ||
        post.username === username
    );

    if (postesUtilisateur.length === 0) return "N/A";

    const tauxEngagementPosts = postesUtilisateur.map((post) => {
      const likes = parseInt(post.likes?.replace(/,/g, "") || 0);
      const comments = parseInt(post.comments?.replace(/,/g, "") || 0);
      const followers = parseInt(
        post.userId?.nbFollowersInsta?.replace(/,/g, "") || 0
      );

      // Si l'utilisateur n'a pas d'abonnés, retourne N/A
      if (!followers) return 0;

      return ((likes + comments) / followers) * 100;
    });

    // Calcul de la moyenne des taux d'engagement
    const moyenneTauxEngagement =
      tauxEngagementPosts.reduce((sum, taux) => sum + taux, 0) /
      tauxEngagementPosts.length;

    return moyenneTauxEngagement.toFixed(2);
  };

  // Fonction pour compter le nombre de posts par utilisateur
  const compterPosts = (utilisateurId, username) => {
    return posts.filter(
      (post) =>
        post.userId?._id === utilisateurId ||
        post.userId === utilisateurId ||
        post.username === username
    ).length;
  };

  // Configuration des colonnes du tableau
  const columns = [
    {
      title: "Nom d'utilisateur",
      dataIndex: "username",
      key: "username",
      ellipsis: true,
      sorter: (a, b) => a.username.localeCompare(b.username),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher par nom"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.username.toLowerCase().includes(value.toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (text, record) => (
        <Space>
          <Avatar
            size="small"
            src={
              record?.avatar
                ? `${process.env.REACT_APP_API_URL}/upload/${record.avatar}`
                : null
            }
            icon={!record.avatar && <UserOutlined />}
          />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Instagram",
      key: "usernameInsta",
      dataIndex: "usernameInsta",
      sorter: (a, b) => a.usernameInsta?.localeCompare(b.usernameInsta || ""),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.usernameInsta?.toLowerCase().includes(value.toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (text) => (
        <Tag icon={<InstagramOutlined />} color="#E1306C">
          @{text || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
      ellipsis: true,
      responsive: ["lg"],
      sorter: (a, b) => a.email?.localeCompare(b.email || ""),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher par email"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.email?.toLowerCase().includes(value.toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (text) => <Text>{text || "N/A"}</Text>,
    },
    {
      title: "Âge",
      key: "age",
      dataIndex: "date",
      responsive: ["md"],
      sorter: (a, b) => calculerAge(a.date) - calculerAge(b.date),
      filters: [
        { text: "18-25", value: "18-25" },
        { text: "26-35", value: "26-35" },
        { text: "36-45", value: "36-45" },
        { text: "46+", value: "46+" },
      ],
      onFilter: (value, record) => {
        const age = calculerAge(record.date);
        if (value === "18-25") return age >= 18 && age <= 25;
        if (value === "26-35") return age >= 26 && age <= 35;
        if (value === "36-45") return age >= 36 && age <= 45;
        if (value === "46+") return age >= 46;
        return false;
      },
      render: (text) => calculerAge(text),
    },
    {
      title: "Genre",
      key: "gender",
      dataIndex: "gender",
      responsive: ["md"],
      filters: [
        { text: "Homme", value: "Homme" },
        { text: "Femme", value: "Femme" },
      ],
      onFilter: (value, record) => record.gender === value,
      render: (text) => (
        <Tag
          icon={text === "Homme" ? <ManOutlined /> : <WomanOutlined />}
          color={text === "Homme" ? "blue" : "magenta"}
        >
          {text || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Abonnés",
      key: "nbFollowersInsta",
      dataIndex: "nbFollowersInsta",
      responsive: ["lg"],
      sorter: (a, b) => {
        const followersA = parseInt(a.nbFollowersInsta?.replace(/,/g, "") || 0);
        const followersB = parseInt(b.nbFollowersInsta?.replace(/,/g, "") || 0);
        return followersA - followersB;
      },
      filters: [
        { text: "< 1,000", value: "<1000" },
        { text: "1,000 - 10,000", value: "1000-10000" },
        { text: "10,000 - 100,000", value: "10000-100000" },
        { text: "> 100,000", value: ">100000" },
      ],
      onFilter: (value, record) => {
        const followers = parseInt(
          record.nbFollowersInsta?.replace(/,/g, "") || 0
        );
        if (value === "<1000") return followers < 1000;
        if (value === "1000-10000")
          return followers >= 1000 && followers < 10000;
        if (value === "10000-100000")
          return followers >= 10000 && followers < 100000;
        if (value === ">100000") return followers >= 100000;
        return false;
      },
      render: (text) => text || "0",
    },
    {
      title: "Posts effectués",
      key: "postsCount",
      responsive: ["xl"],
      render: (_, record) => compterPosts(record._id, record.usernameInsta),
      sorter: (a, b) =>
        compterPosts(a._id, a.usernameInsta) -
        compterPosts(b._id, b.usernameInsta),
      filters: [
        { text: "0", value: "0" },
        { text: "1-5", value: "1-5" },
        { text: "6-10", value: "6-10" },
        { text: "> 10", value: ">10" },
      ],
      onFilter: (value, record) => {
        const count = compterPosts(record._id, record.usernameInsta);
        if (value === "0") return count === 0;
        if (value === "1-5") return count >= 1 && count <= 5;
        if (value === "6-10") return count >= 6 && count <= 10;
        if (value === ">10") return count > 10;
        return false;
      },
    },
    {
      title: "Taux d'engagement",
      key: "engagementRate",
      responsive: ["xl"],
      render: (_, record) => {
        const taux = calculerTauxEngagement(record._id, record.usernameInsta);
        return taux === "N/A" ? taux : `${taux}%`;
      },
      sorter: (a, b) => {
        const rateA =
          parseFloat(calculerTauxEngagement(a._id, a.usernameInsta)) || 0;
        const rateB =
          parseFloat(calculerTauxEngagement(b._id, b.usernameInsta)) || 0;
        return rateA - rateB;
      },
      filters: [
        { text: "< 1%", value: "<1" },
        { text: "1% - 3%", value: "1-3" },
        { text: "3% - 6%", value: "3-6" },
        { text: "> 6%", value: ">6" },
      ],
      onFilter: (value, record) => {
        const rate =
          parseFloat(
            calculerTauxEngagement(record._id, record.usernameInsta)
          ) || 0;
        if (value === "<1") return rate < 1;
        if (value === "1-3") return rate >= 1 && rate < 3;
        if (value === "3-6") return rate >= 3 && rate < 6;
        if (value === ">6") return rate >= 6;
        return false;
      },
    },
    {
      title: "Ville",
      key: "ville",
      dataIndex: "ville",
      responsive: ["lg"],
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher par ville"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.ville?.toLowerCase().includes(value.toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (text, record) => (
        <Tooltip
          title={`${text || "Inconnu"} ${
            record.codePostal ? `(${record.codePostal})` : ""
          }`}
        >
          <Tag icon={<EnvironmentOutlined />} color="blue">
            {text || "Inconnu"}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: "Statut",
      key: "status",
      dataIndex: "status",
      responsive: ["md"],
      filters: [
        { text: "Actif", value: "active" },
        { text: "Inactif", value: "inactive" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Badge
          status={status === "active" ? "success" : "error"}
          text={status === "active" ? "Actif" : "Inactif"}
        />
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={() => showUserDetails(record)}
          >
            Détails
          </Button>
          <Popconfirm
            title={
              record.status === "active"
                ? "Désactiver cet utilisateur ?"
                : "Activer cet utilisateur ?"
            }
            onConfirm={() => toggleUserStatus(record)}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              size="small"
              type={record.status === "active" ? "default" : "primary"}
              danger={record.status === "active"}
              icon={
                record.status === "active" ? (
                  <StopOutlined />
                ) : (
                  <CheckCircleOutlined />
                )
              }
            >
              {record.status === "active" ? "Désactiver" : "Activer"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const showUserDetails = async (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);

    // Filtrer les posts pour cet utilisateur
    const filteredPosts = posts.filter(
      (post) =>
        post.userId?._id === user._id ||
        post.userId === user._id ||
        post.username === user.usernameInsta
    );

    setUserPosts(filteredPosts);
  };

  const toggleUserStatus = async (user) => {
    const newStatus = user.status === "active" ? "inactive" : "active";
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/user/${user._id}`, {
        status: newStatus,
      });
      setUsers((prev) =>
        prev.map((u) => (u._id === user._id ? { ...u, status: newStatus } : u))
      );
      notification.success({
        message: newStatus === "active" ? "Utilisateur activé" : "Utilisateur désactivé",
      });
    } catch (error) {
      notification.error({
        message: "Erreur",
        description: error.message || "Impossible de modifier le statut",
      });
    }
  };

  const fetchPosts = async () => {
    try {
      setPostsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Post`);
      if (response.data) {
        setPosts(response.data);
      }
    } catch (error) {
      notification.error({
        message: "Erreur de chargement des posts",
        description:
          error.message || "Impossible de récupérer les données des posts",
      });
      console.error("Erreur de récupération des posts:", error);
    } finally {
      setPostsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user`);
      if (response.data) {
        setUsers(response.data);
      } else {
        notification.error({
          message: "Aucune donnée trouvée",
          description: "Impossible de récupérer les données utilisateurs",
        });
      }
    } catch (error) {
      notification.error({
        message: "Erreur de chargement des données",
        description: error.message || "Veuillez réessayer plus tard",
      });
      console.error("Erreur de récupération des utilisateurs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchUsers();
      await fetchPosts();
    };
    fetchData();
  }, []);

  return (
    <div className="tabled">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<Title level={4}>Utilisateurs Instagram</Title>}
            extra={
              <Button
                type="primary"
                onClick={() => {
                  fetchUsers();
                  fetchPosts();
                }}
              >
                Actualiser
              </Button>
            }
          >
            {isLoading || postsLoading ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <p>Chargement des données...</p>
              </div>
            ) : users.length === 0 ? (
              <Empty description="Aucun utilisateur trouvé" />
            ) : (
              <Table
                columns={columns}
                dataSource={users}
                pagination={{
                  pageSize: 10,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total} utilisateurs`,
                  showSizeChanger: false,
                  size: "small",
                }}
                rowKey="_id"
                bordered
                size="middle"
                scroll={{ x: "max-content" }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <ModalDetailsUtilisateur
        visible={isModalOpen}
        user={selectedUser}
        posts={userPosts}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

const ModalDetailsUtilisateur = ({ visible, user, posts, onClose }) => {
  if (!user) return null;

  // Calcul des statistiques
  const totalPosts = posts.length;

  const getLikesCount = (likesStr) => {
    if (!likesStr) return 0;
    return parseInt(likesStr.replace(/,/g, "")) || 0;
  };

  const getCommentsCount = (commentsStr) => {
    if (!commentsStr) return 0;
    return parseInt(commentsStr.replace(/,/g, "")) || 0;
  };

  const totalLikes = posts.reduce(
    (sum, post) => sum + getLikesCount(post.likes),
    0
  );

  const totalComments = posts.reduce(
    (sum, post) => sum + getCommentsCount(post.comments),
    0
  );

  const followers = parseInt(user?.nbFollowersInsta?.replace(/,/g, "") || 0);

  // Calcul du taux d'engagement moyen
  let tauxEngagementMoyen = "N/A";
  if (totalPosts > 0 && followers > 0) {
    tauxEngagementMoyen = (
      ((totalLikes + totalComments) / totalPosts / followers) *
      100
    ).toFixed(2);
  }

  // Calcul de l'âge
  const calculerAge = (dateNaissance) => {
    if (!dateNaissance) return "N/A";
    const ajd = new Date();
    const dateNaiss = new Date(dateNaissance);
    let age = ajd.getFullYear() - dateNaiss.getFullYear();
    const mois = ajd.getMonth() - dateNaiss.getMonth();
    if (mois < 0 || (mois === 0 && ajd.getDate() < dateNaiss.getDate())) {
      age--;
    }
    return age;
  };

  // Compter les posts par statut
  const validPostsCount = posts.filter(
    (post) => post.status === "Valid"
  ).length;

  const pendingPostsCount = posts.filter(
    (post) => post.status === "Pending"
  ).length;

  const rejectedPostsCount = posts.filter(
    (post) => post.status !== "Valid" && post.status !== "Pending"
  ).length;

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined /> Détails de l'utilisateur
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
      ]}
      width={700}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* En-tête utilisateur */}
        <Space
          align="center"
          style={{ width: "100%", justifyContent: "center" }}
        >
          <Avatar
            size={80}
            src={
              user?.avatar ? `${process.env.REACT_APP_API_URL}/upload/${user.avatar}` : null
            }
            icon={!user?.avatar && <UserOutlined />}
            style={{ backgroundColor: "#1890ff" }}
          />
          <Space direction="vertical" size={0}>
            <Title level={3} style={{ margin: 0 }}>
              {user?.username}
            </Title>
            <Space wrap>
              <Tag icon={<InstagramOutlined />} color="#E1306C">
                @{user?.usernameInsta}
              </Tag>
              <Tag color={user?.status === "active" ? "green" : "red"}>
                {user?.status === "active" ? "Actif" : "Inactif"}
              </Tag>
              {user?.gender && (
                <Tag
                  icon={
                    user?.gender === "Homme" ? (
                      <ManOutlined />
                    ) : (
                      <WomanOutlined />
                    )
                  }
                  color={user?.gender === "Homme" ? "blue" : "magenta"}
                >
                  {user?.gender}
                </Tag>
              )}
            </Space>
          </Space>
        </Space>

        <Divider orientation="left">Statistiques Instagram</Divider>
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Abonnés"
                value={user?.nbFollowersInsta || "0"}
                prefix={<UserOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Posts"
                value={totalPosts}
                prefix={<FileImageOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Taux d'engagement"
                value={
                  tauxEngagementMoyen === "N/A"
                    ? tauxEngagementMoyen
                    : `${tauxEngagementMoyen}%`
                }
                prefix={<LikeOutlined />}
                valueStyle={{ color: "#fa541c" }}
              />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Détails des posts</Divider>
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Total J'aime"
                value={totalLikes.toLocaleString()}
                prefix={<LikeOutlined />}
                valueStyle={{ color: "#fa541c" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Total Commentaires"
                value={totalComments.toLocaleString()}
                prefix={<CommentOutlined />}
                valueStyle={{ color: "#13c2c2" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic title="Statut des posts" value="" />
              <div style={{ marginTop: 10 }}>
                <Tag color="green">Validés: {validPostsCount}</Tag>
                <Tag color="gold">En attente: {pendingPostsCount}</Tag>
                <Tag color="red">Rejetés: {rejectedPostsCount}</Tag>
              </div>
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Informations personnelles</Divider>
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Genre">
            {user?.gender || "Non spécifié"}
          </Descriptions.Item>
          <Descriptions.Item label="Âge">
            {calculerAge(user?.date)}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {user?.email || "Non spécifié"}
          </Descriptions.Item>
          <Descriptions.Item label="Téléphone">
            {user?.phone || "Non spécifié"}
          </Descriptions.Item>
          <Descriptions.Item label="Localisation" span={2}>
            {user?.ville
              ? `${user?.ville}${
                  user?.codePostal ? ` (${user?.codePostal})` : ""
                }`
              : "Non spécifié"}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Informations du compte</Divider>
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Type de compte">
            <Tag color={user?.type === "admin" ? "gold" : "default"}>
              {user?.type === "admin" ? "Administrateur" : "Utilisateur"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            <Badge
              status={user?.status === "active" ? "success" : "error"}
              text={user?.status === "active" ? "Actif" : "Inactif"}
            />
          </Descriptions.Item>
          <Descriptions.Item label="ID Utilisateur" span={2}>
            <Text code copyable>
              {user?._id}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Modal>
  );
};

export default Customers;
